import React, { useState } from 'react';
import { usePIM } from '../../context/PIMContext';
import { Modal } from '../ui/Modal';
import { Input } from '../ui/Input';
import { PrimaryButton } from '../ui/PrimaryButton';
import { Plus, Check, Loader2, Package, Tag, Box, Info } from 'lucide-react';
import { PRODUCT_TYPES } from '../../config';

export const CreateProductModal = ({ isOpen, onClose }) => {
    const { saveProduct, statuses } = usePIM();
    const [isLoading, setIsLoading] = useState(false);

    // Form State
    const [formData, setFormData] = useState({
        sku: '',
        model_name: '',
        factory_model: '',
        ean: '',
        description: '',
        product_types: [],
        initial_charge: {
            batch_number: '',
            status: 'Planned',
            order_number: ''
        }
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name.startsWith('charge_')) {
            const field = name.replace('charge_', '');
            setFormData(prev => ({
                ...prev,
                initial_charge: { ...prev.initial_charge, [field]: value }
            }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleTypeChange = (type) => {
        setFormData(prev => {
            const currentTypes = prev.product_types || [];
            if (currentTypes.includes(type)) {
                return { ...prev, product_types: currentTypes.filter(t => t !== type) };
            } else {
                return { ...prev, product_types: [...currentTypes, type] };
            }
        });
    };

    const handleCreate = async () => {
        if (!formData.sku || !formData.model_name) return;

        setIsLoading(true);
        const payload = {
            sku: formData.sku,
            model_name: formData.model_name,
            factory_model: formData.factory_model,
            ean: formData.ean,
            description: formData.description,
            product_types: formData.product_types,
            initial_charge: formData.initial_charge.batch_number ? formData.initial_charge : null,
            status: 'Draft',
            is_accessory: formData.product_types.includes('accessory') ? 1 : 0
        };

        const success = await saveProduct(payload);
        setIsLoading(false);
        if (success) {
            onClose();
            setFormData({
                sku: '',
                model_name: '',
                factory_model: '',
                ean: '',
                description: '',
                product_types: [],
                initial_charge: { batch_number: '', status: 'Planned', order_number: '' }
            });
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Create New Product" size="2xl">
            <div className="space-y-6">

                {/* Basic Info Section */}
                <div className="space-y-4 bg-stone-900/50 p-4 rounded-lg border border-stone-800">
                    <div className="flex items-center gap-2 text-blue-400 mb-2">
                        <Package size={16} />
                        <h3 className="text-sm font-bold uppercase tracking-wider">Basic Information</h3>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1">
                            <label className="text-xs font-bold text-stone-500 uppercase">SKU <span className="text-red-500">*</span></label>
                            <Input
                                name="sku"
                                value={formData.sku}
                                onChange={handleChange}
                                placeholder="e.g. PROD-001"
                                className="font-mono"
                                autoFocus
                            />
                        </div>
                        <div className="space-y-1">
                            <label className="text-xs font-bold text-stone-500 uppercase">Model Name <span className="text-red-500">*</span></label>
                            <Input
                                name="model_name"
                                value={formData.model_name}
                                onChange={handleChange}
                                placeholder="e.g. Super Widget"
                            />
                        </div>
                        <div className="space-y-1">
                            <label className="text-xs font-bold text-stone-500 uppercase">Factory Model</label>
                            <Input
                                name="factory_model"
                                value={formData.factory_model}
                                onChange={handleChange}
                                placeholder="Internal code"
                                className="font-mono text-xs"
                            />
                        </div>
                        <div className="space-y-1">
                            <label className="text-xs font-bold text-stone-500 uppercase">EAN / Barcode</label>
                            <Input
                                name="ean"
                                value={formData.ean}
                                onChange={handleChange}
                                placeholder="GTIN-13"
                            />
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-6">
                    {/* Product Types */}
                    <div className="space-y-4 bg-stone-900/50 p-4 rounded-lg border border-stone-800">
                        <div className="flex items-center gap-2 text-purple-400 mb-2">
                            <Tag size={16} />
                            <h3 className="text-sm font-bold uppercase tracking-wider">Classification</h3>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {PRODUCT_TYPES.filter(t => t !== 'normal').map(type => (
                                <label key={type} className={`
                                    flex items-center gap-2 px-3 py-2 rounded cursor-pointer border transition select-none text-xs font-medium uppercase
                                    ${formData.product_types.includes(type)
                                        ? 'bg-purple-900/30 border-purple-600 text-purple-200'
                                        : 'bg-stone-950 border-stone-700 text-stone-400 hover:bg-stone-900'
                                    }
                                `}>
                                    <input
                                        type="checkbox"
                                        checked={formData.product_types.includes(type)}
                                        onChange={() => handleTypeChange(type)}
                                        className="hidden"
                                    />
                                    {type.replace('_', ' ')}
                                </label>
                            ))}
                        </div>
                    </div>

                    {/* Initial Charge */}
                    <div className="space-y-4 bg-stone-900/50 p-4 rounded-lg border border-stone-800">
                        <div className="flex items-center gap-2 text-green-400 mb-2">
                            <Box size={16} />
                            <h3 className="text-sm font-bold uppercase tracking-wider">Initial Charge</h3>
                        </div>

                        <div className="space-y-3">
                            <div className="space-y-1">
                                <label className="text-[10px] font-bold text-stone-500 uppercase">Batch Number <span className="text-red-500">*</span></label>
                                <Input
                                    name="charge_batch_number"
                                    placeholder="e.g. BATCH-001"
                                    value={formData.initial_charge.batch_number}
                                    onChange={handleChange}
                                    className="bg-stone-950 border-stone-700 py-1.5 w-full text-stone-200 placeholder:text-stone-600"
                                />
                            </div>

                            {/* Order Number */}
                            <div className="space-y-1">
                                <label className="text-[10px] font-bold text-stone-500 uppercase">Order Number</label>
                                <Input
                                    name="charge_order_number"
                                    placeholder="e.g. PO-123456"
                                    value={formData.initial_charge.order_number}
                                    onChange={handleChange}
                                    className="bg-stone-950 border-stone-700 py-1.5 w-full text-stone-200 placeholder:text-stone-600"
                                />
                            </div>

                            <div className="space-y-1">
                                <label className="text-[10px] font-bold text-stone-500 uppercase">Status</label>
                                <select
                                    name="charge_status"
                                    value={formData.initial_charge.status}
                                    onChange={handleChange}
                                    className="w-full bg-stone-950 border border-stone-700 rounded px-3 py-1.5 text-sm text-stone-200 focus:outline-none focus:border-blue-600 appearance-none"
                                >
                                    {statuses.map(s => (
                                        <option key={s.id || s} value={s.name || s}>
                                            {s.name || s}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t border-stone-800">
                    <button onClick={onClose} className="px-4 py-2 rounded text-stone-400 hover:text-white hover:bg-stone-800 transition text-sm font-medium">
                        Cancel
                    </button>
                    <PrimaryButton
                        onClick={handleCreate}
                        disabled={!formData.sku || !formData.model_name || !formData.initial_charge.batch_number || isLoading}
                        className="px-6 py-2"
                    >
                        {isLoading ? <Loader2 size={16} className="animate-spin mr-2" /> : <Plus size={16} className="mr-2" />}
                        Create Product
                    </PrimaryButton>
                </div>

            </div>
        </Modal>
    );
};
